import { Link } from 'react-router-dom';
import {
  Calculator, Bot, FolderOpen, Receipt, Landmark, Users, Headphones, Check,
} from 'lucide-react';
import MarketingShell from '../components/homepage/MarketingShell';

const CATEGORIES = [
  {
    icon: Calculator, color: '#27B84A', title: 'Accounting & Bookkeeping',
    tagline: 'Real double-entry accounting without the accounting degree.',
    points: ['Chart of accounts, journal entries & general ledger', 'P&L, balance sheet & cash flow reports', 'Recurring entries & batch imports', 'Audit log on every change'],
  },
  {
    icon: Bot, color: '#8B5CF6', title: 'AI Assistant',
    tagline: '240+ AI models that actually know your numbers.',
    points: ['ChatGPT, Claude, Gemini, Perplexity & more in one chat', 'Ask questions about your real financial data', 'AI document reading & smart categorization', 'CFO Buck: forecasts, insights & recommendations'],
  },
  {
    icon: FolderOpen, color: '#3B82F6', title: 'Documents & OCR',
    tagline: 'Every receipt, statement and contract — organized for you.',
    points: ['Drag-and-drop uploads with smart folders', 'OCR scanning pulls totals, dates & vendors automatically', 'Email-forwarding inbox for receipts', 'Folder templates & client-sharing links'],
  },
  {
    icon: Receipt, color: '#FF7816', title: 'Invoicing & Payments',
    tagline: 'Send branded invoices and get paid without chasing.',
    points: ['Branded invoices in your colors & logo', 'Online payments via Stripe', 'Recurring invoices & payment reminders', 'Bills, vendors & accounts payable'],
  },
  {
    icon: Landmark, color: '#14B8A6', title: 'Banking & Reconciliation',
    tagline: 'Connect your accounts. Match every transaction.',
    points: ['Secure bank connections via Plaid', 'Statement uploads & smart matching', 'Split transactions & categorization rules', 'Month-end reconciliation workflows'],
  },
  {
    icon: Users, color: '#EAB308', title: 'Client Portal & eSignatures',
    tagline: 'A professional branded portal your clients will love.',
    points: ['Branded client logins on your own URL', 'Share documents, invoices & checklists', 'Legally-binding eSignatures built in', 'Client messaging & task checklists'],
  },
  {
    icon: Headphones, color: '#8B5CF6', title: 'Priority Support',
    tagline: 'Software plus real humans who have your back.',
    points: ['Fast, friendly human support', 'Tax savings reports & strategy guidance', 'Guided onboarding & QuickBooks migration', 'Time tracking & practice tools included'],
  },
];

export default function FeaturesHub() {
  return (
    <MarketingShell title="Features | CountBucks">
      <section className="mx-auto max-w-7xl px-4 pb-20 pt-14 sm:px-6" data-testid="features-hub-page">
        <span className="inline-block rounded-full border border-[#27B84A]/40 bg-[#F2FBF4] px-4 py-1.5 text-xs font-bold tracking-widest text-[#118A35] dark:bg-[#0E1A11] dark:text-[#27B84A]">
          EVERYTHING INCLUDED
        </span>
        <h1 className="mt-5 max-w-3xl text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
          One platform. <span className="text-[#27B84A]">Every tool</span> your business needs.
        </h1>
        <p className="mt-4 max-w-2xl text-base text-[#596273] sm:text-lg dark:text-[#AEB6B1]">
          Everything below is included in every CountBucks plan — no add-ons, no per-tool pricing, no juggling logins.
        </p>

        <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            return (
              <div
                key={cat.title}
                data-testid={`features-hub-card-${cat.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                className="flex flex-col rounded-3xl border border-[#E4E8E6] bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-black/5 dark:border-[#1C2420] dark:bg-[#10150F]"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl" style={{ backgroundColor: `${cat.color}1A` }}>
                  <Icon className="h-6 w-6" style={{ color: cat.color }} />
                </span>
                <h2 className="mt-4 text-lg font-bold">{cat.title}</h2>
                <p className="mt-1 text-sm text-[#596273] dark:text-[#8B948F]">{cat.tagline}</p>
                <ul className="mt-4 space-y-2.5">
                  {cat.points.map((p) => (
                    <li key={p} className="flex items-start gap-2.5 text-sm text-[#18202D] dark:text-[#D9DED9]">
                      <span className="mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-[#27B84A]">
                        <Check className="h-3 w-3 text-white" strokeWidth={3} />
                      </span>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
          <div className="flex flex-col items-start justify-center rounded-3xl bg-[#118A35] p-7 text-white">
            <h2 className="text-2xl font-extrabold leading-tight">All of it. Starting at $19.99/mo.</h2>
            <p className="mt-3 text-sm text-white/85">Try every feature free for 7 days, then $19.99/mo. Cancel before day 8 and pay $0.</p>
            <Link
              to="/login?mode=register&plan=starter&cadence=monthly"
              data-testid="features-hub-cta"
              className="mt-6 inline-flex rounded-2xl bg-white px-6 py-3 text-sm font-bold text-[#118A35] transition-transform hover:-translate-y-0.5"
            >
              Start Your Free 7-Day Trial
            </Link>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
